import { DownSquareOutlined, UpSquareOutlined } from "@ant-design/icons";
import {
    Alert,
    Checkbox,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Switch,
    Typography,
    message,
} from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as API from "../../../../networking/guesses";
import { createPolicy } from "../../../../redux/actions/policies";
import { CLEAR_POLICY_ERROR } from "../../../../redux/actions/types";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { PolicyLine } from "../../../../redux/reducers/types/actuaryTypes";

const { Title } = Typography;
const { TextArea } = Input;

export default function CreatePolicyModal({
    isVisible,
    setIsVisible,
    initialSearchQuery = "",
}: {
    isVisible: boolean;
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    initialSearchQuery?: string;
}) {
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const createPolicyPending = useAppSelector((state) => state.policies.createPolicyPending);
    const createPolicyError = useAppSelector((state) => state.policies.createPolicyError);
    const userPolicies = useAppSelector((state) => state.policies.userPolicies);
    const [policyLines, setPolicyLines] = useState<PolicyLine[]>([]);
    const [loadingPolicyLines, setLoadingPolicyLines] = useState(false);
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
    const [similarItems, setSimilarItems] = useState<{ item: PolicyLine; similarity: number }[]>([]);

    const handleOk = () => {
        form.validateFields()
            .then((values) => {
                dispatch(
                    createPolicy({
                        ...values,
                        coverage_start_date:
                            values.coverage_start_date.format(),
                        premium_pool_type: "perpetual_pool",
                        governance_type: "direct_democracy",
                        ...(!values.available_underlying_insured_types && {
                            available_underlying_insured_types: [
                                "cell_phone",
                                "audio_equipment",
                            ],
                        }),
                    })
                );
            })
            .catch((info) => {
                console.log("Validate Failed:", info);
            });
    };

    const handleCancel = () => {
        dispatch({ type: CLEAR_POLICY_ERROR });
        setIsVisible(false);
    };

    const toggleAdvanced = () => {
        setShowAdvancedSettings((v) => !v);
    };

    useEffect(() => {
        if (isVisible) {
            setSearchQuery(initialSearchQuery);
            form.resetFields();
            dispatch({ type: CLEAR_POLICY_ERROR });
            fetchPolicyLines();
            if (initialSearchQuery) {
                searchSimilarPolicyLines(initialSearchQuery);
                const policyName = `${initialSearchQuery} insurance`;
                form.setFieldsValue({ name: policyName });
            }
        }
    }, [isVisible, initialSearchQuery, dispatch, form]);

    useEffect(() => {
        if (createPolicyError) {
            const errors = createPolicyError.errors || {};
            
            const fieldMap: Record<string, string> = {
                name: "name",
                description: "description",
                coverage_type: "coverage_type",
                coverage_start_date: "coverage_start_date",
                coverage_duration: "coverage_duration",
                available_underlying_insured_types: "available_underlying_insured_types",
                is_public: "is_public",
            };

            const fieldErrors: Record<string, string[]> = {};
            
            Object.keys(errors).forEach((key) => {
                const fieldName = fieldMap[key] || key;
                if (errors[key]) {
                    if (Array.isArray(errors[key])) {
                        fieldErrors[fieldName] = errors[key];
                    } else if (typeof errors[key] === "string") {
                        fieldErrors[fieldName] = [errors[key]];
                    } else if (errors[key] && typeof errors[key] === "object") {
                        const errorMsgs = Object.values(errors[key]).flat();
                        fieldErrors[fieldName] = errorMsgs.map((msg) => String(msg));
                    }
                }
            });

            form.setFields(
                Object.keys(fieldErrors).map((field) => ({
                    name: field,
                    errors: fieldErrors[field],
                }))
            );
        }
    }, [createPolicyError, form]);

    useEffect(() => {
        if (!createPolicyPending && !createPolicyError && isVisible && userPolicies.length > 0) {
            const latestPolicy = userPolicies[userPolicies.length - 1];
            if (latestPolicy && latestPolicy.created_at) {
                const createdAt = new Date(latestPolicy.created_at).getTime();
                const now = Date.now();
                if (now - createdAt < 2000) {
                    try {
                        setIsVisible(false);
                        form.resetFields();
                        message.success("Policy created successfully");
                        navigate(`/policy/${latestPolicy.id}`);
                    } catch (error) {
                        console.error("Error navigating to policy:", error);
                        setIsVisible(false);
                        form.resetFields();
                        message.success("Policy created successfully");
                        window.location.href = `/policy/${latestPolicy.id}`;
                    }
                }
            }
        }
    }, [createPolicyPending, createPolicyError, isVisible, userPolicies, form, setIsVisible, navigate]);

    const fetchPolicyLines = async () => {
        setLoadingPolicyLines(true);
        try {
            const response = await API.getAvailablePolicyLines();
            setPolicyLines(response.data.results || response.data);
        } catch (error) {
            console.error("Error fetching policy lines:", error);
            message.error("Failed to load insurable items");
        } finally {
            setLoadingPolicyLines(false);
        }
    };

    const searchSimilarPolicyLines = async (query: string) => {
        if (!query.trim()) {
            setSimilarItems([]);
            return;
        }
        try {
            const response = await API.searchSimilarPolicyLines(query, 0.7);
            setSimilarItems(response.data.similar_items || []);
        } catch (error) {
            console.error("Error searching similar policy lines:", error);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        if (value) {
            searchSimilarPolicyLines(value);
        } else {
            setSimilarItems([]);
        }
    };

    const handleCreateNewType = async (newTypeName: string, existingValues: string[] = []) => {
        try {
            const response = await API.createPolicyLine({ name: newTypeName });
            const newPolicyLine = response.data;
            setPolicyLines([newPolicyLine, ...policyLines]);
            const currentValues = existingValues.length > 0 ? existingValues : (form.getFieldValue("available_underlying_insured_types") || []);
            form.setFieldsValue({
                available_underlying_insured_types: [...currentValues, newPolicyLine.name],
            });
            message.success(`Created new insurable item: ${newTypeName}`);
            setSearchQuery("");
            setSimilarItems([]);
        } catch (error: any) {
            console.error("Error creating policy line:", error);
            message.error(error.response?.data?.detail || "Failed to create new insurable item");
        }
    };

    const getSelectOptions = () => {
        let options: { label: string; value: string }[] = [];

        // If there's a search query, show similar items first, then all items
        if (searchQuery && searchQuery.trim()) {
            // Add similar items first
            const similarItemNames = new Set(similarItems.map((item) => item.item.name));
            similarItems.forEach((item) => {
                options.push({
                    label: item.item.name,
                    value: item.item.name,
                });
            });

            // Add remaining items that weren't in similar items
            policyLines.forEach((line) => {
                if (!similarItemNames.has(line.name)) {
                    options.push({
                        label: line.name,
                        value: line.name,
                    });
                }
            });

            // If no similar items found, add option to create new
            if (similarItems.length === 0) {
                const queryLower = searchQuery.toLowerCase();
                const exactMatch = policyLines.some((line) => line.name.toLowerCase() === queryLower);
                if (!exactMatch) {
                    options.push({
                        label: `Create "${searchQuery}"`,
                        value: `__create__${searchQuery}`,
                    });
                }
            }
        } else {
            // No search query, show all items
            options = policyLines.map((line) => ({
                label: line.name,
                value: line.name,
            }));
        }

        return options;
    };

    const handleSelectChange = (values: string[]) => {
        // Filter out create actions and handle them separately
        const createActions = values.filter((v) => v.startsWith("__create__"));
        const normalValues = values.filter((v) => !v.startsWith("__create__"));

        // Set normal values first
        form.setFieldsValue({
            available_underlying_insured_types: normalValues,
        });

        // Handle create actions - pass normalValues so the new item is added to them
        createActions.forEach((action) => {
            const newTypeName = action.replace("__create__", "");
            handleCreateNewType(newTypeName, normalValues);
        });
    };

    return (
        <Modal
            title="Create a policy"
            open={isVisible}
            onOk={handleOk}
            okText="Create"
            confirmLoading={createPolicyPending}
            onCancel={handleCancel}
        >
            <Form form={form} layout="vertical" requiredMark={false}>
                {createPolicyError && (
                    <Alert
                        message="Unable to create policy"
                        description={createPolicyError.message}
                        type="error"
                        closable
                        onClose={() => {
                            dispatch({ type: CLEAR_POLICY_ERROR });
                        }}
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Form.Item label="Policy name" name="name" required>
                    <Input placeholder="Phone Insurance" />
                </Form.Item>
                <Form.Item 
                    label="Description" 
                    name="description" 
                    required
                    rules={[{ required: true, message: "Please enter a description" }]}
                >
                    <TextArea placeholder="A group of friends insuring themselves against..." />
                </Form.Item>
                <Form.Item
                    label="Policy type"
                    name="coverage_type"
                    initialValue={"m_property"}
                    required
                >
                    <Select>
                        <Select.Option value="m_property">
                            Minor Property
                        </Select.Option>
                        <Select.Option value="renters" disabled>
                            Renter's Insurance
                        </Select.Option>
                    </Select>
                </Form.Item>
                <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                >
                    <Space size={16}>
                        <Form.Item
                            label="Coverage start date"
                            name="coverage_start_date"
                            required
                        >
                            <DatePicker format={"MMMM Do, YYYY"} />
                        </Form.Item>
                        <Form.Item
                            label="Coverage length"
                            tooltip="How long coverage lasts from start date in months"
                            required
                            name="coverage_duration"
                            initialValue={12}
                        >
                            <InputNumber
                                min={3}
                                max={36}
                                placeholder="Months"
                            />
                        </Form.Item>
                    </Space>
                </div>
                <Form.Item
                    label="Make the policy public?"
                    tooltip="Allow other users to view and join the policy"
                    name="is_public"
                    status="success"
                >
                    <Switch
                        checkedChildren={"Yes"}
                        unCheckedChildren={"No"}
                        defaultChecked
                        size="default"
                    />
                </Form.Item>

                <Divider />
                <Row
                    justify="space-between"
                    onClick={toggleAdvanced}
                    style={{
                        cursor: "pointer",
                    }}
                >
                    <Title level={4}>Advanced Settings</Title>
                    <div
                        style={{
                            padding: "2px 10px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 5,
                        }}
                    >
                        {showAdvancedSettings ? (
                            <DownSquareOutlined />
                        ) : (
                            <UpSquareOutlined />
                        )}
                    </div>
                </Row>
                {showAdvancedSettings && (
                    <div>
                        <Form.Item
                            label="Insurable items"
                            name="available_underlying_insured_types"
                        >
                            <Select
                                mode="multiple"
                                showSearch
                                placeholder="Search or type to add insurable items"
                                loading={loadingPolicyLines}
                                options={getSelectOptions()}
                                filterOption={false}
                                onSearch={handleSearchChange}
                                onChange={handleSelectChange}
                                searchValue={searchQuery}
                                notFoundContent={
                                    searchQuery && similarItems.length === 0
                                        ? `No similar items found. Type "${searchQuery}" and select "Create" to add it.`
                                        : null
                                }
                            />
                        </Form.Item>
                        <Form.Item
                            label="Governance type"
                            name="governance_type"
                            initialValue={"direct_democracy"}
                            hidden
                        >
                            <Select>
                                <Select.Option value="direct_democracy">
                                    Direct Democracy
                                </Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Premium pool type"
                            name="premium_pool_type"
                            initialValue={"perpetual_pool"}
                            hidden
                        >
                            <Select>
                                <Select.Option value="perpetual_pool">
                                    Perpetual Pool
                                </Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Maximum number of policy members"
                            name={"max_pod_size"}
                            tooltip="Leave blank to allow an unlimited number of people to join."
                        >
                            <InputNumber min={2} />
                        </Form.Item>
                        <Form.Item
                            label="Allow members to join after the policy starts"
                            name={"allow_joiners_after_policy_start"}
                            tooltip="Allow people to join the policy after the start date"
                        >
                            <Checkbox />
                        </Form.Item>
                    </div>
                )}
            </Form>
        </Modal>
    );
}
